import type { OperationalJsonValue, OperationalCompleteness, OperationalPhaseDocument } from "./operational-ai.types";

export interface CatalogProduct {
  [key: string]: OperationalJsonValue;
  id: string;
  name: string;
  sku: string;
  currency: string;
  price: number | null;
  salePrice: number | null;
  promoDescription: string;
  mainImageUrl: string;
  description: string;
  instructions: string;
  images: string[];
  paquetes: Array<Record<string, OperationalJsonValue>>;
  revisarInventario: string; // "vender_sin_stock" | "consultar_stock"
}

export function readCatalogProducts(value: OperationalJsonValue | undefined): CatalogProduct[] {
  if (!Array.isArray(value)) return [];
  const list: CatalogProduct[] = [];
  for (const item of value) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const p = item as Record<string, unknown>;
    if (
      typeof p.id !== "string" ||
      typeof p.name !== "string" ||
      typeof p.description !== "string" ||
      typeof p.instructions !== "string" ||
      typeof p.sku !== "string" ||
      typeof p.currency !== "string"
    ) {
      continue;
    }
    const price = p.price === null || (typeof p.price === "number" && Number.isFinite(p.price) && p.price >= 0) ? p.price : null;
    const salePrice = p.salePrice === null || (typeof p.salePrice === "number" && Number.isFinite(p.salePrice) && p.salePrice >= 0) ? p.salePrice : null;
    const promoDescription = typeof p.promoDescription === "string" ? p.promoDescription : "";
    const mainImageUrl = typeof p.mainImageUrl === "string" ? p.mainImageUrl : "";
    const images = Array.isArray(p.images) ? p.images.filter((img): img is string => typeof img === "string" && /^https?:\/\//.test(img)) : [];

    const rawPaquetes = Array.isArray(p.paquetes) ? p.paquetes : [];
    const paquetes = rawPaquetes.filter((pkg) => typeof pkg === "object" && pkg !== null) as Array<Record<string, OperationalJsonValue>>;
    const revisarInventario = typeof p.revisarInventario === "string" ? p.revisarInventario : "consultar_stock";

    list.push({
      id: p.id,
      name: p.name,
      sku: p.sku,
      currency: p.currency,
      price,
      salePrice,
      promoDescription,
      mainImageUrl,
      description: p.description,
      instructions: p.instructions,
      images,
      paquetes,
      revisarInventario,
      noAplicaDescription: p.noAplicaDescription === true,
      noAplicaInstructions: p.noAplicaInstructions === true,
      noAplicaImages: p.noAplicaImages === true,
    });
  }
  return list;
}

export function catalogCompleteness(phases: OperationalPhaseDocument[]): OperationalCompleteness | null {
  const entries = Object.assign({}, ...phases.map((phase) => phase.fields)) as OperationalPhaseDocument["fields"];
  if (!entries["catalogo.productos"]) return null;
  const missing: string[] = [];
  const entry = entries["catalogo.productos"];
  const products = readCatalogProducts(entry.value);
  const validList = Array.isArray(entry.value) && products.length === entry.value.length && products.length > 0 && products.length <= 100
    && new Set(products.map((product) => product.id)).size === products.length;
  if (!validList || !["confirmado", "corregido"].includes(entry.status)) missing.push("catalogo.productos");
  for (const product of products) if (!product.id.trim() || !product.name.trim() || !product.description.trim() || product.images.length > 20) missing.push(`catalogo.productos.${product.id}`);
  const total = Math.max(products.length, 1);
  return { percentage: Math.max(0, Math.round((total - missing.length) / total * 100)), criticalMissing: missing,
    completedPhases: [
      ...(!missing.some((key) => key.startsWith("catalogo.")) ? ["producto" as const] : [])] };
}
